/** @jsxImportSource @emotion/react */
import { css, type CSSObject, type Interpolation, type Theme } from '@emotion/react';
import type {
  ButtonHTMLAttributes,
  ComponentProps,
  CSSProperties,
  ReactNode,
} from 'react';

import { pxToRem } from '@/Client/Styles/pxToRem';
import { CommonField, CommonFieldAdornment, CommonFieldInput, CommonFieldLabel, CommonFieldShell, CommonFieldTextarea } from '@kaiizen/kai-shiki/react';
import { StyledForm } from '../StyledForm';
import { MODAL_COLORS } from './ModalStyleLib';

/** Emotion's `css` prop type. kai-shiki components take `styleOverride` for plain styles. */
type EmotionCss = Interpolation<Theme>;

const FIELD_STYLE = {
  position: 'relative',
  minHeight: 53,
  padding: '20px 12px 14px',
  border: `1px solid ${MODAL_COLORS.textFieldLabel}`,
  borderRadius: 5,
  background: MODAL_COLORS.white,
  display: 'flex',
  alignItems: 'center',
} satisfies CSSObject;

const FIELD_LABEL_STYLE = {
  position: 'absolute',
  top: -7,
  left: 10,
  zIndex: 1,
  padding: '0 2px',
  background: MODAL_COLORS.white,
  fontFamily: 'CircularXX, sans-serif',
  fontSize: pxToRem(10),
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: 1.6,
  letterSpacing: '0.2px',
  color: MODAL_COLORS.textFieldLabel,
} satisfies CSSObject;

const MODAL_TEXT_FIELD_STYLE = {
  position: 'relative',
  width: '100%',
  height: 53,
} satisfies CSSObject;

const MODAL_TEXT_FIELD_ROOT_STYLE = {
  width: '100%',
  height: 53,
} satisfies CSSProperties;

const MODAL_TEXT_FIELD_BOX_STYLE = {
  border: `1px solid ${MODAL_COLORS.textFieldLabel}`,
  borderRadius: 5,
  background: MODAL_COLORS.white,
} satisfies CSSObject;

const FIELD_INPUT_TEXT_STYLE = {
  width: '100%',
  border: 0,
  padding: 0,
  background: 'transparent',
  outline: 'none',
  fontFamily: 'CircularXX, sans-serif',
  fontSize: pxToRem(12),
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: 1.333,
  letterSpacing: '0.24px',
  color: MODAL_COLORS.text,
} satisfies CSSObject;

const TEXTAREA_STYLE = {
  ...FIELD_INPUT_TEXT_STYLE,
  resize: 'none',
  minHeight: 16,
} satisfies CSSObject;

const CHARACTER_COUNT_STYLE = {
  position: 'absolute',
  top: 23,
  right: 10,
  fontFamily: 'CircularXX, sans-serif',
  fontSize: pxToRem(10),
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: 1.6,
  letterSpacing: '0.2px',
  color: MODAL_COLORS.textFieldLabel,
} satisfies CSSObject;

const SELECT_BUTTON_STYLE = css`
  label: SELECT_BUTTON_STYLE;
  width: 100%;
  border: 0;
  padding: 0;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  cursor: pointer;
  color: ${MODAL_COLORS.text};
`;

const SELECT_TEXT_STYLE = css`
  label: SELECT_TEXT_STYLE;
  ${FIELD_INPUT_TEXT_STYLE}
  text-align: left;
`;

const FORM_STYLE = css`
  label: FORM_STYLE;
  margin-top: 20px;
  gap: 20px;
`;

export const getModalCharacterCountLabel = (value: string, maxLength: number) => `${value.length}/${maxLength}`;

export const ModalForm = (props: ComponentProps<typeof StyledForm>) => <StyledForm css={FORM_STYLE} {...props} />;

export const ModalFieldShell = ({
  css: fieldCss,
  ...props
}: ComponentProps<typeof CommonFieldShell> & { css?: EmotionCss }) => <CommonFieldShell css={[FIELD_STYLE, fieldCss]} {...props} />;

type ModalTextFieldProps =
  & Omit<
    ComponentProps<typeof CommonField>,
    'inputProps' | 'label' | 'value'
  >
  & {
    label: ReactNode;
    inputProps: NonNullable<ComponentProps<typeof CommonField>['inputProps']>;
    characterCountLabel?: ReactNode;
    characterCountCss?: EmotionCss;
    css?: EmotionCss;
  };

export const ModalTextField = ({
  label,
  inputProps,
  characterCountLabel,
  characterCountCss,
  css: fieldCss,
  boxCss,
  labelCss,
  valueCss,
  ...props
}: ModalTextFieldProps) => (
  <div css={[MODAL_TEXT_FIELD_STYLE, fieldCss]}>
    <CommonField
      label={label}
      inputProps={inputProps}
      style={MODAL_TEXT_FIELD_ROOT_STYLE}
      boxCss={[MODAL_TEXT_FIELD_BOX_STYLE, boxCss]}
      labelCss={[FIELD_LABEL_STYLE, labelCss]}
      valueCss={[
        FIELD_INPUT_TEXT_STYLE,
        characterCountLabel ? { paddingRight: 48 } : undefined,
        valueCss,
      ]}
      {...props}
    />
    {characterCountLabel && (
      <CommonFieldAdornment css={[CHARACTER_COUNT_STYLE, characterCountCss]}>
        {characterCountLabel}
      </CommonFieldAdornment>
    )}
  </div>
);

export const ModalFieldLabel = ({
  css: fieldCss,
  ...props
}: ComponentProps<typeof CommonFieldLabel> & { css?: EmotionCss }) => <CommonFieldLabel css={[FIELD_LABEL_STYLE, fieldCss]} {...props} />;

export const ModalTextInput = ({
  css: fieldCss,
  ...props
}: ComponentProps<typeof CommonFieldInput> & { css?: EmotionCss }) => <CommonFieldInput css={[FIELD_INPUT_TEXT_STYLE, fieldCss]} {...props} />;

export const ModalTextarea = ({
  css: fieldCss,
  ...props
}: ComponentProps<typeof CommonFieldTextarea> & { css?: EmotionCss }) => <CommonFieldTextarea css={[TEXTAREA_STYLE, fieldCss]} {...props} />;

export const ModalCharacterCount = ({
  css: fieldCss,
  ...props
}: ComponentProps<typeof CommonFieldAdornment> & { css?: EmotionCss }) => <CommonFieldAdornment css={[CHARACTER_COUNT_STYLE, fieldCss]} {...props} />;

export const ModalSelectButton = ({
  children,
  trailingAdornment,
  ...buttonProps
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  trailingAdornment?: ReactNode;
}) => (
  <button type='button' css={SELECT_BUTTON_STYLE} {...buttonProps}>
    <span css={SELECT_TEXT_STYLE}>{children}</span>
    {trailingAdornment}
  </button>
);
